/**
 * BankLoadsDrawer.tsx — drill-in drawer showing the last 30 days of
 * tblDataLoad rows for one bank.
 *
 * Pulls data via the banks slice's `fetchBankLoads` thunk so multiple opens
 * of the same bank don't double-fetch (the slice caches per orgId).
 */

import React, { useEffect } from 'react';

import Drawer from '../../components/Drawer';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import Spinner from '../../components/Spinner';
import { formatDate, formatDateTime } from '../../helpers/dateFormat';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
	fetchBankLoads,
	selectBankLoads,
	selectBankLoadsError,
	selectBankLoadsStatus,
} from '../../redux/slices/banks';
import type { BankLoadEntry, BankSubmissionRow } from '../../type/api';

interface Props {
	bank: BankSubmissionRow | null;
	onClose: () => void;
}

function statusPillClass(s: BankLoadEntry['status']): string {
	switch (s) {
		case 'SUCCESS':
			return 'green';
		case 'FAILED':
			return 'red';
		case 'PARTIAL':
			return 'amber';
		case 'STARTED':
		default:
			return 'grey';
	}
}

function fromIso(daysAgo: number): string {
	const d = new Date();
	d.setDate(d.getDate() - daysAgo);
	return d.toISOString().slice(0, 10);
}

export default function BankLoadsDrawer({ bank, onClose }: Props) {
	const dispatch = useAppDispatch();
	const orgId = bank?.orgId ?? -1;
	const loads = useAppSelector(selectBankLoads(orgId));
	const status = useAppSelector(selectBankLoadsStatus(orgId));
	const error = useAppSelector(selectBankLoadsError(orgId));

	useEffect(() => {
		if (!bank) return;
		// Always re-fetch on open so the user sees fresh data — the slice
		// cache only de-dupes within an in-flight request, not across opens.
		dispatch(
			fetchBankLoads({
				orgId: bank.orgId,
				from: fromIso(30),
			}),
		);
	}, [bank, dispatch]);

	const handleRetry = () => {
		if (bank) dispatch(fetchBankLoads({ orgId: bank.orgId, from: fromIso(30) }));
	};

	return (
		<Drawer
			open={Boolean(bank)}
			onClose={onClose}
			title={bank ? bank.orgName : ''}
			subtitle={
				bank ? (
					<span>
						ID {bank.orgId} · last 30 days of <code>tblDataLoad</code>
					</span>
				) : null
			}
			width={600}
		>
			{status === 'loading' && <Spinner label="Loading load history…" />}
			{status === 'error' && (
				<ErrorBanner message={error || 'Failed to load history'} onRetry={handleRetry} />
			)}
			{status === 'success' && (!loads || loads.length === 0) && (
				<EmptyState
					title="No loads in the last 30 days"
					hint="This bank hasn't submitted any LOAN / DEPOSIT / KYC files recently."
				/>
			)}
			{status === 'success' && loads && loads.length > 0 && (
				<table className="tbl" style={{ width: '100%' }}>
					<thead>
						<tr>
							<th scope="col">Report date</th>
							<th scope="col">Type</th>
							<th scope="col">Status</th>
							<th scope="col" className="num">Records</th>
							<th scope="col">Completed</th>
						</tr>
					</thead>
					<tbody>
						{loads.map((row) => (
							<tr key={row.loadId}>
								<td style={{ fontVariantNumeric: 'tabular-nums' }}>
									{formatDate(row.reportDate)}
								</td>
								<td>
									<span style={{ fontWeight: 600 }}>{row.dumpType}</span>
									<div
										style={{
											fontSize: 10,
											color: 'var(--text-faint)',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: 240,
										}}
										title={row.fileName}
									>
										{row.fileName}
									</div>
								</td>
								<td>
									<span className={`status-pill ${statusPillClass(row.status)}`}>
										{row.status}
									</span>
									{row.errorMessage && (
										<div
											style={{
												fontSize: 11,
												color: 'var(--bad-600)',
												marginTop: 2,
												maxWidth: 220,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
											title={row.errorMessage}
										>
											{row.errorMessage}
										</div>
									)}
								</td>
								<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
									{row.recordsLoaded ?? '—'}
									{typeof row.recordsSkipped === 'number' && row.recordsSkipped > 0 && (
										<div style={{ fontSize: 10, color: 'var(--warn-600)' }}>
											{row.recordsSkipped} skipped
										</div>
									)}
								</td>
								<td style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
									{row.completedAt ? formatDateTime(row.completedAt) : '—'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Drawer>
	);
}
